import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import {
  formatDate,
  formatDateIstConvter,
} from "../CommonComponents/formatter";
import LoadingComponent from "../CommonComponents/loadinComponent";
import moment from "moment";
import DataTable from "../UserManagement/SubUserManagement/Datatable";
import "../UserManagement/SubUserManagement/subUser.css";
import { TripsActionV2 } from "../../actions/TripsV2";
import { FaFilePdf } from "react-icons/fa";
import { FaFileExcel } from "react-icons/fa6";
import { Button } from "@material-ui/core";
import {
  Autocomplete as MuiAutocomplete,
  TextField as MuiTextField,
} from "@mui/material";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { LocksAction } from "../../actions/lock";
import DownloadIcon from "@mui/icons-material/Download";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import Dialog from "@material-ui/core/Dialog";
import { GoogleMap, Marker } from "@react-google-maps/api";
import ReportLayoutPage from "../ReportsGps/ReportLayoutPage";
import LockFilter from "./Lockfilter";

const useStyles = makeStyles((theme) => ({
  customTooltip: {
    maxWidth: 320,
    backgroundColor: "#1e293b !important",
    color: "#ffffff !important",
    fontSize: "0.875rem",
    padding: "16px !important",
    borderRadius: "14px !important",
    lineHeight: "1.6",
  },
  tooltipPopper: {
    pointerEvents: "auto !important",
  },
}));

const datePresetOptions = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "This Week", value: "thisWeek" },
  { label: "Last Week", value: "lastWeek" },
  { label: "This Month", value: "thisMonth" },
  { label: "Last Month", value: "lastMonth" },
];

// ============ MAP MODAL COMPONENT ============
const MapModal = ({
  open,
  onClose,
  lat,
  lng,
  label,
  classes,
  eventTime,
  eventType,
}) => {
  const [isReady, setIsReady] = useState(false);
  const [address, setAddress] = useState("");
  const [copied, setCopied] = useState(false);

  const parsedLat = parseFloat(lat);
  const parsedLng = parseFloat(lng);
  const hasValidCoords =
    !isNaN(parsedLat) &&
    !isNaN(parsedLng) &&
    (parsedLat !== 0 || parsedLng !== 0);

  const center = hasValidCoords
    ? { lat: parsedLat, lng: parsedLng }
    : { lat: 28.6139, lng: 77.209 };

  const buildLockMarkerIcon = useCallback((lockData) => {
    const isOpen = lockData?.isLockOpen;
    const SIZE = 52;
    const canvas = document.createElement("canvas");
    canvas.width = SIZE;
    canvas.height = SIZE + 10;
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 3, 0, Math.PI * 2);
    ctx.fillStyle = isOpen ? "#f92516" : "#0ea5e9";
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 24px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(isOpen ? "🔓" : "🔒", SIZE / 2, SIZE / 2);
    return {
      url: canvas.toDataURL(),
      scaledSize: new window.google.maps.Size(SIZE, SIZE + 10),
      anchor: new window.google.maps.Point(SIZE / 2, SIZE + 9),
    };
  }, []);

  const markerIcon = useMemo(() => {
    if (!isReady || !window.google?.maps) return null;
    return buildLockMarkerIcon({
      isLockOpen: eventType?.toUpperCase() === "OPEN",
    });
  }, [isReady, eventType, buildLockMarkerIcon]);

  useEffect(() => {
    if (!open) return;
    const check = () => {
      if (window.google?.maps) {
        setIsReady(true);
      } else {
        setTimeout(check, 100);
      }
    };
    check();
  }, [open]);

  useEffect(() => {
    if (!isReady || !hasValidCoords) return;

    fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${parsedLat}&lon=${parsedLng}&format=json`,
      { headers: { "Accept-Language": "en" } },
    )
      .then((res) => res.json())
      .then((data) => {
        setAddress(data?.display_name || `${parsedLat}, ${parsedLng}`);
      })
      .catch(() => {
        setAddress(`${parsedLat}, ${parsedLng}`);
      });
  }, [isReady, parsedLat, parsedLng]);

  const getEventColor = () => {
    if (eventType?.toUpperCase() === "OPEN") return "#ef4444";
    if (eventType?.toUpperCase() === "CLOSE") return "#10b981";
    return "#082D6B";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 20,
          overflow: "hidden",
          boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
        },
      }}
    >
      {/* Header */}
      <div
        style={{
          background: getEventColor(),
          padding: "18px 20px",
          display: "flex",
          alignItems: "flex-start",
          gap: 14,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-40%",
            right: "-5%",
            width: 200,
            height: 200,
            background:
              "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        {/* Icon badge */}
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            flexShrink: 0,
            background: "rgba(255,255,255,0.16)",
            border: "1.5px solid rgba(255,255,255,0.28)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LocationOnIcon style={{ color: "#fff", fontSize: 22 }} />
        </div>

        {/* Title block */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 6,
            }}
          >
            <span
              style={{
                fontSize: 15.5,
                fontWeight: 700,
                color: "#fff",
                letterSpacing: 0.2,
                textDecoration: "none",
              }}
            >
              Lock Event Location
            </span>
            {eventType && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  padding: "3px 10px",
                  borderRadius: 20,
                  background: "rgba(255,255,255,0.18)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  color: "#fff",
                  whiteSpace: "nowrap",
                }}
              >
                {eventType?.toUpperCase()}
              </span>
            )}
          </div>

          {/* Time row */}
          {eventTime && (
            <div
              style={{
                fontSize: 13,
                color: "rgba(255,255,255,0.75)",
                display: "flex",
                alignItems: "center",
                gap: 5,
                marginBottom: 5,
              }}
            >
              <span style={{ fontSize: 12 }}>🕐</span> {eventTime}
            </div>
          )}

          {/* Address row - always visible, no hidden tooltip */}
          {hasValidCoords && (
            <div
              style={{
                fontSize: 12.5,
                color: "rgba(255,255,255,0.65)",
                display: "flex",
                alignItems: "flex-start",
                gap: 5,
                lineHeight: 1.4,
              }}
            >
              <span style={{ flexShrink: 0, fontSize: 12, marginTop: 1 }}>
                📍
              </span>
              <span
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {address || "Fetching address…"}
              </span>
            </div>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            flexShrink: 0,
            background: "rgba(255,255,255,0.14)",
            border: "1px solid rgba(255,255,255,0.24)",
            color: "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 15,
            fontWeight: 700,
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = "rgba(255,255,255,0.26)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "rgba(255,255,255,0.14)")
          }
        >
          ✕
        </button>
      </div>

      {/* Map area */}
      <div style={{ height: 430, position: "relative" }}>
        {isReady ? (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={center}
            zoom={hasValidCoords ? 15 : 5}
            options={{
              streetViewControl: false,
              mapTypeControl: true,
              fullscreenControl: true,
              zoomControl: true,
            }}
          >
            {hasValidCoords && markerIcon && (
              <Marker position={center} icon={markerIcon} zIndex={1000} />
            )}
            {hasValidCoords && !markerIcon && (
              <Marker
                position={center}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 10,
                  fillColor: getEventColor(),
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 2,
                }}
              />
            )}
          </GoogleMap>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 10,
              color: "rgba(8,45,107,0.4)",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Loading map…
          </div>
        )}

        {!hasValidCoords && isReady && (
          <div
            style={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(0,0,0,0.65)",
              color: "#fff",
              borderRadius: 8,
              padding: "6px 14px",
              fontSize: 11,
              fontWeight: 600,
              backdropFilter: "blur(4px)",
              whiteSpace: "nowrap",
            }}
          >
            No coordinates available
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          background: "#f8fafc",
          borderTop: "1px solid #e8eeff",
          padding: "12px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <span
          style={{
            fontSize: 11.5,
            color: "#94a3b8",
            fontFamily: "monospace",
            whiteSpace: "nowrap",
          }}
        >
          {hasValidCoords
            ? `📍 ${parsedLat.toFixed(6)}, ${parsedLng.toFixed(6)}`
            : "📍 Coordinates unavailable"}
        </span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          {address && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(address);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              title="Copy address"
              style={{
                background: copied ? "#f0fdf4" : "#fff",
                color: copied ? "#16a34a" : "#64748b",
                border: `1px solid ${copied ? "#bbf7d0" : "#e2e8f0"}`,
                borderRadius: 8,
                padding: "7px 14px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 5,
                transition: "all 0.2s ease",
              }}
            >
              {copied ? "✅ Copied!" : "📋 Copy Address"}
            </button>
          )}
          {hasValidCoords && (
            <button
              onClick={() =>
                window.open(
                  `https://maps.google.com/?q=${parsedLat},${parsedLng}`,
                  "_blank",
                )
              }
              style={{
                background: getEventColor(),
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "7px 14px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 5,
                transition: "opacity 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              Open in Google Maps
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

// ============ FILTER COMPONENT ============
const Filter = ({
  setExportData,
  exportData,
  setItems,
  setLoading,
  setReportDates,
  extraActions,
  locks,
  locksLoading,
  page,
  rowsPerPage,
  setPage,
  setTotalCount,
}) => {
  const handleExport = async (type) => {
    const fromMoment = moment(from);
    const toMoment = moment(to);
    if (!exportData || exportData.length === 0) {
      alert("No data to export. Please generate a report first.");
      return;
    }
    const payload = {
      fromDate: fromMoment.valueOf(),
      toDate: toMoment.valueOf(),
      lockId: lockFilter?.lockId || null,
      isExcelReport: type === "excel",
    };
    setLoading(true);
    try {
      const responseText = await TripsActionV2.exportLockUnlockReport(payload);
      if (!responseText) {
        alert("Export failed. Please try again.");
        return;
      }
      let base64 = "";
      try {
        const json = JSON.parse(responseText);
        base64 = json?.data || json?.fileData || json?.base64 || json;
      } catch (e) {
        base64 = responseText;
      }
      if (typeof base64 !== "string" || base64.length < 10) {
        alert("Export failed: invalid file data received");
        return;
      }
      base64 = base64.replace(/^data:.*,/, "").replace(/\s/g, "");
      const mimeType =
        type === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf";
      const ext = type === "excel" ? "xlsx" : "pdf";
      try {
        const byteChars = atob(base64);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++)
          byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `LockUnlock_Report_${moment().format("YYYY-MM-DD_HH-mm")}.${ext}`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (atobErr) {
        console.error("Base64 decoding error:", atobErr);
        alert("Export failed: could not decode file data.");
      }
    } catch (err) {
      console.error("Export error:", err);
      alert("Export failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const [from, setFrom] = useState(moment().startOf("day"));
  const [to, setTo] = useState(moment().endOf("day"));
  const [lockFilter, setLockFilter] = useState(null);
  const [datePreset, setDatePreset] = useState(datePresetOptions[0]);

  const filterRef = useRef({ from, to, lockFilter });
  useEffect(() => {
    filterRef.current = { from, to, lockFilter };
  }, [from, to, lockFilter]);

  const handleDatePresetChange = (option) => {
    setDatePreset(option);
    let selectedFrom, selectedTo;
    switch (option.value) {
      case "today":
        selectedFrom = moment().startOf("day");
        selectedTo = moment().endOf("day");
        break;
      case "yesterday":
        selectedFrom = moment().subtract(1, "day").startOf("day");
        selectedTo = moment().subtract(1, "day").endOf("day");
        break;
      case "thisWeek":
        selectedFrom = moment().startOf("week");
        selectedTo = moment().endOf("day");
        break;
      case "lastWeek":
        selectedFrom = moment().subtract(1, "week").startOf("week");
        selectedTo = moment().subtract(1, "week").endOf("week");
        break;
      case "thisMonth":
        selectedFrom = moment().startOf("month");
        selectedTo = moment().endOf("day");
        break;
      case "lastMonth":
        selectedFrom = moment().subtract(1, "month").startOf("month");
        selectedTo = moment().subtract(1, "month").endOf("month");
        break;
      default:
        return;
    }
    if (selectedTo.isBefore(selectedFrom)) {
      selectedTo = selectedFrom.clone().endOf("day");
    }
    setFrom(selectedFrom);
    setTo(selectedTo);
  };

  const fetchData = async (pageNo, pageSz, fromDate, toDate, lock) => {
    setLoading(true);
    const fromMoment = moment(fromDate);
    const toMoment = moment(toDate);

    if (toMoment.isBefore(fromMoment)) {
      alert("To Date cannot be less than From Date");
      setLoading(false);
      return;
    }

    if (toMoment.diff(fromMoment, "months", true) > 1) {
      alert("Date difference should not be more than 1 month");
      setLoading(false);
      return;
    }

    const userData = JSON.parse(localStorage.getItem("data"));

    const payload = {
      userId: userData?.id,
      lockId: lock?.lockId || null,
      fromDate: fromMoment.valueOf(),
      toDate: toMoment.valueOf(),
      pageNo: pageNo,
      pageSize: pageSz,
    };
    try {
      const response = await TripsActionV2.getLockUnlockReports(payload);

      setReportDates({ from: fromDate, to: toDate });

      const resData = response?.data;

      if (resData && Array.isArray(resData.data)) {
        setTotalCount(resData.totalElements || 0);

        const mapped = resData.data.map((event, idx) => ({
          id: pageNo * pageSz + idx + 1,
          lockNumber: event?.lockNumber || "—",
          serialNumber: event?.serialNumber || "—",
          openedType:event?.openedType || "—",
          lockId: event?.lockId,
          gpsRefId: event?.gpsRefId,
          eventType: event?.eventType || "—",
          eventTime: event?.tripEndTime
            ? formatDateIstConvter(event?.tripEndTime)
            : event.tripStartTime
              ? formatDateIstConvter(event?.tripStartTime)
              : "—",
          tripEndTime: event.tripEndTime,
          startLat: event?.endLat ?? event?.startLat ?? 0,
          startLon: event?.endLon ?? event?.startLon ?? 0,
          raw: event,
        }));

        setItems(mapped);
        setExportData(mapped);
      } else {
        setItems([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Error fetching lock/unlock report:", err);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!lockFilter) {
      alert("Please select a lock before generating the report.");
      return;
    }

    setPage(0);
    fetchData(0, rowsPerPage, from, to, lockFilter);
  };

  useEffect(() => {
    if (filterRef.current) {
    }
  }, []);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const { from: f, to: t, lockFilter: l } = filterRef.current;
    fetchData(page, rowsPerPage, f, t, l);
  }, [page, rowsPerPage]);

  return (
    <div style={{ padding: "16px", background: "#fff", borderRadius: "10px" }}>
      <div
        style={{
          display: "flex",
          gap: 12,
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {/* Date Preset Dropdown */}
          <MuiAutocomplete
            size="small"
            options={datePresetOptions}
            value={datePreset}
            getOptionLabel={(option) => option.label}
            onChange={(e, newValue) => {
              if (newValue) handleDatePresetChange(newValue);
            }}
            disableClearable
            sx={{ width: 160 }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                placeholder="Today"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    fontSize: 13,
                    "& fieldset": { borderColor: "#d1d5db" },
                    "&:hover fieldset": { borderColor: "#9ca3af" },
                    "&.Mui-focused fieldset": { borderColor: "#2563eb" },
                  },
                }}
              />
            )}
          />

          {/* From Date */}
          <div>
            <input
              type="datetime-local"
              value={from.format(moment.HTML5_FMT.DATETIME_LOCAL)}
              onChange={(e) => setFrom(moment(e.target.value))}
              style={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                fontSize: 13,
                width: 200,
              }}
            />
          </div>

          {/* To Date */}
          <div>
            <input
              type="datetime-local"
              value={to.format(moment.HTML5_FMT.DATETIME_LOCAL)}
              onChange={(e) => setTo(moment(e.target.value))}
              min={from.format(moment.HTML5_FMT.DATETIME_LOCAL)}
              style={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                fontSize: 13,
                width: 200,
              }}
            />
          </div>

          {/* Lock Filter */}
          <div>
            <LockFilter
              locks={locks}
              locksLoading={locksLoading}
              lockFilter={lockFilter}
              setLockFilter={setLockFilter}
            />
          </div>

          {/* Generate Button */}
          <Button
            variant="contained"
            style={{
              borderRadius: 8,
              height: 36,
              minWidth: 100,
              background: "#082D6B",
            }}
            onClick={handleSubmit}
          >
            Show
          </Button>
        </div>

        {/* RIGHT SECTION - Columns (extraActions), Excel, and PDF Export Buttons */}
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          {/* Columns button with dropdown - from extraActions */}
          {extraActions}

          {/* Excel Export Button */}
          <Button
            variant="contained"
            startIcon={<FaFileExcel style={{ fontSize: 16 }} />}
            endIcon={<DownloadIcon />}
            onClick={() => handleExport("excel")}
            style={{
              height: 36,
              padding: "0 16px",
              background: "linear-gradient(135deg, #064e3b 0%, #047857 100%)",
              color: "#fff",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Excel
          </Button>

          {/* PDF Export Button */}
          <Button
            variant="contained"
            startIcon={<FaFilePdf style={{ fontSize: 16 }} />}
            endIcon={<DownloadIcon />}
            onClick={() => handleExport("pdf")}
            style={{
              height: 36,
              padding: "0 16px",
              background: "linear-gradient(135deg, #7f1d1d 0%, #b91c1c 100%)",
              color: "#fff",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============
const LockUnlockReport = (props) => {
  const classes = useStyles();
  const colSettingsKey = "lock_unlock_report_col_settings";

  const [items, setItems] = useState([]);
  const [isLoading, setLoading] = React.useState(false);
  const [exportData, setExportData] = React.useState([]);
  const [reportDates, setReportDates] = React.useState({
    from: null,
    to: null,
  });
  const [locks, setLocks] = useState([]);
  const [locksLoading, setLocksLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem(colSettingsKey);
    return saved
      ? JSON.parse(saved)
      : {
          id: true,
          lockNumber: true,
          serialNumber: true,
          eventType: true,
          openedType:true,
          eventTime: true,
          // status: true,
          map: true,
        };
  });

  const [showColSettings, setShowColSettings] = useState(false);
  const colSettingsRef = useRef(null);

  // Map modal state
  const [mapModal, setMapModal] = useState({
    open: false,
    lat: null,
    lng: null,
    eventType: null,
    eventTime: null,
  });

  // Fetch locks on component mount
  useEffect(() => {
    const fetchLocks = async () => {
      try {
        setLocksLoading(true);
        const res = await LocksAction.getLockList({ pageNo: 0, pageSize: 0 });
        if (res.data && Array.isArray(res.data)) {
          setLocks(res.data);
        }
      } catch (e) {
        console.error("Lock fetch error:", e);
      } finally {
        setLocksLoading(false);
      }
    };
    fetchLocks();
  }, []);

  const toggleColumn = (colId) => {
    setVisibleColumns((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      localStorage.setItem(colSettingsKey, JSON.stringify(next));
      return next;
    });
  };

  // Close column settings on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        colSettingsRef.current &&
        !colSettingsRef.current.contains(e.target)
      ) {
        setShowColSettings(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Define available columns
  const allColumns = [
    { id: "id", label: "S. No." },
    { id: "lockNumber", label: "Lock Number" },
    { id: "serialNumber", label: "Serial Number" },
    { id: "eventType", label: "Event Type" },
     { id: "openedType", label: "Open Type" },
    { id: "eventTime", label: "Event Time" },
    // { id: "status", label: "Status" },
    { id: "map", label: "Map" },
  ];

  // render: (row) => (
  //         <div>
  //           <div>
  //             {row.lastPollingDate &&
  //               moment(row.lastPollingDate).format("DD-MM-YYYY")}
  //           </div>
  //           {row.lastPollingDate &&
  //             moment(row.lastPollingDate).format("HH:mm:ss")}
  //         </div>
  //       ),
  const columns = allColumns
    .filter((col) => visibleColumns[col.id])
    .map((col) => {
      if (col.id === "eventType") {
        return {
          ...col,
          render: (row) => {
            const color =
              row.eventType?.toUpperCase() === "OPEN" ? "#ef4444" : "#10b981";
            const bg =
              row.eventType?.toUpperCase() === "OPEN"
                ? "rgba(239,68,68,0.15)"
                : "rgba(16,185,129,0.15)";
            return (
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: 6,
                  background: bg,
                  color: color,
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                {row.eventType}
              </span>
            );
          },
        };
      }
      //   if (col.id === "eventTime") {
      //     return {
      //       ...col,
      //       render: (row) => {
      //         console.log(row,"rowszdxcfgvbhjn")
      //         return (
      //           <div>
      //             {row?.eventTime &&
      //               formatDate(row.eventTime, "DD-MM-YYYY hh:mm A")}
      //           </div>
      //         );
      //       },
      //     };
      //   }
      if (col.id === "map") {
        return {
          ...col,
          render: (row) =>
            row.startLat &&
            row.startLon &&
            (row.startLat !== 0 || row.startLon !== 0) ? (
              <button
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#082D6B",
                  fontSize: 20,
                }}
                title="View on map"
                onClick={() =>
                  setMapModal({
                    open: true,
                    lat: row.startLat,
                    lng: row.startLon,
                    eventType: row.eventType,
                    eventTime: row.eventTime,
                  })
                }
              >
                <LocationOnIcon />
              </button>
            ) : (
              <span style={{ color: "#9ca3af" }}>—</span>
            ),
        };
      }
      return col;
    });

  return (
    <div className="sub-user-list-root" style={{ position: "relative" }}>
      <LoadingComponent isLoading={isLoading} />
      <div
        className="page-title-row"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 20,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            className="page-back-btn"
            onClick={() => window.open("/stats-dashboard", "_self")}
            aria-label="Go back"
          >
            <ArrowBackIcon className="page-back-icon" />
          </button>
          <span className="page-title-text">
            Digital Lock - Lock/Unlock Report
          </span>
        </div>
      </div>

      <Container>
        <ReportLayoutPage
          filter={
            <Filter
              setExportData={setExportData}
              exportData={exportData}
              setLoading={setLoading}
              setItems={setItems}
              setReportDates={setReportDates}
              locks={locks}
              locksLoading={locksLoading}
              page={page}
              rowsPerPage={rowsPerPage}
              setPage={setPage}
              setTotalCount={setTotalCount}
              extraActions={
                <div className="gf-col-settings-wrap" ref={colSettingsRef}>
                  <button
                    className={`gf-col-settings-btn ${showColSettings ? "active" : ""}`}
                    onClick={() => setShowColSettings(!showColSettings)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      height: 40,
                      padding: "0 16px",
                      border: "none",
                      borderRadius: 10,
                      background: showColSettings
                        ? "linear-gradient(135deg, #0f172a 0%, #1e293b 30%, #334155 60%, #475569 100%)"
                        : "linear-gradient(135deg, #1e293b 0%, #334155 30%, #475569 60%, #64748b 100%)",
                      color: "#ffffff",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      boxShadow: showColSettings
                        ? "0 6px 24px rgba(30, 41, 59, 0.55)"
                        : "0 4px 16px rgba(30, 41, 59, 0.4)",
                      letterSpacing: "0.3px",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      transform: showColSettings
                        ? "translateY(-1px)"
                        : "translateY(0)",
                    }}
                  >
                    <svg
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      width="16"
                      height="16"
                    >
                      <path
                        fillRule="evenodd"
                        d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Columns
                  </button>
                  {showColSettings && (
                    <div className="gf-col-dropdown">
                      <div className="gf-col-dropdown-header">
                        Toggle Columns
                      </div>
                      {allColumns.map((col) => (
                        <label key={col.id} className="gf-col-dropdown-item">
                          <input
                            type="checkbox"
                            className="gf-col-checkbox"
                            checked={!!visibleColumns[col.id]}
                            onChange={() => toggleColumn(col.id)}
                          />
                          {col.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              }
            />
          }
        >
          <Paper className="sub-user-list-paper">
            <DataTable
              columns={columns}
              rows={items || []}
              rowKey={(row) => row.id}
              tableId="lock-unlock-report-table"
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              emptyState={
                <div style={{ textAlign: "center", padding: "4rem 2rem" }}>
                  <div style={{ fontSize: "48px", color: "#bdbdbd" }}>🔐</div>
                  <div
                    style={{
                      color: "#757575",
                      fontSize: "18px",
                      fontWeight: 500,
                    }}
                  >
                    No lock/unlock events found
                  </div>
                  <div
                    style={{
                      color: "#9e9e9e",
                      fontSize: "14px",
                      marginTop: "4px",
                    }}
                  >
                    Select a lock and date range, then click Generate
                  </div>
                </div>
              }
              showRowMenu={false}
            />
          </Paper>
        </ReportLayoutPage>
      </Container>

      {/* Map Modal */}
      <MapModal
        open={mapModal.open}
        onClose={() =>
          setMapModal({
            open: false,
            lat: null,
            lng: null,
            eventType: null,
            eventTime: null,
          })
        }
        lat={mapModal.lat}
        lng={mapModal.lng}
        eventType={mapModal.eventType}
        eventTime={mapModal.eventTime}
        classes={classes}
      />
    </div>
  );
};

export default LockUnlockReport;
